'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ACADEMIC_PALETTE_PRESETS,
  DEFAULT_ACADEMIC_PALETTE_PREFERENCES,
  type AcademicPalettePreset,
  type AcademicPalettePreferences,
} from '@/app/lib/academicPalette';
import { DEFAULT_SEMESTER_ID, isSemesterId, type SemesterId } from '@/app/lib/semesters';
import {
  DEFAULT_EXPLORER_PREFERENCES,
  EXPLORER_PREFERENCES_STORAGE_KEY,
  LEGACY_EXPLORER_VIEW_STORAGE_KEY,
  isExplorerViewMode,
  parseExplorerPreferences,
  type ExplorerPreferences,
  type ExplorerViewMode,
} from '@/app/lib/explorerPreferences';

type UiPreferencesContextValue = {
  activeSemester: SemesterId;
  setActiveSemester: (semester: string) => void;
  preferredSchedulesBySemester: Record<string, string>;
  setPreferredScheduleForSemester: (semester: string, scheduleId: string) => void;
  isSidebarPinned: boolean;
  /** False until this device has stored an explicit sidebar choice (absent in older mocks = treat as saved). */
  sidebarPreferenceSaved?: boolean;
  setSidebarPinned: (pinned: boolean) => void;
  toggleSidebarPin: () => void;
  academicPalettePreferences: AcademicPalettePreferences;
  academicPalettePresets: AcademicPalettePreset[];
  setAcademicPalettePreferences: (preferences: AcademicPalettePreferences) => void;
  setCoursePaletteId: (paletteId: string) => void;
  setProgramPaletteId: (paletteId: string) => void;
  addCustomAcademicPalette: (preset: AcademicPalettePreset) => void;
  deleteCustomAcademicPalette: (paletteId: string) => void;
  resetAcademicPalettePreferences: () => void;
  explorerPreferences: ExplorerPreferences;
  explorerPreferencesHydrated: boolean;
  setExplorerDefaultView: (view: ExplorerViewMode) => void;
  setExplorerDepartmentColorsEnabled: (enabled: boolean) => void;
};

const UiPreferencesContext = createContext<UiPreferencesContextValue | undefined>(
  undefined
);

const SIDEBAR_STORAGE_KEY = 'sidebar-pinned';
const ACTIVE_SEMESTER_STORAGE_KEY = 'scarlet-active-semester';
const SEMESTER_SCHEDULE_STORAGE_KEY = 'scarlet-semester-schedule-preferences';
const ACADEMIC_PALETTE_STORAGE_KEY = 'scarlet-academic-palette-preferences';
const CUSTOM_ACADEMIC_PALETTES_STORAGE_KEY = 'scarlet-custom-academic-palettes';

function isAcademicPalettePreferences(
  value: unknown
): value is AcademicPalettePreferences {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<AcademicPalettePreferences>;
  return (
    typeof candidate.coursePaletteId === 'string' &&
    candidate.coursePaletteId.length > 0 &&
    typeof candidate.programPaletteId === 'string' &&
    candidate.programPaletteId.length > 0
  );
}

function isAcademicPaletteSwatch(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return ['id', 'label', 'solid', 'soft', 'border', 'text', 'contrastText'].every(
    (key) => typeof candidate[key] === 'string' && (candidate[key] as string).length > 0
  );
}

function isAcademicPalettePreset(value: unknown): value is AcademicPalettePreset {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<AcademicPalettePreset>;
  return (
    typeof candidate.id === 'string' &&
    candidate.id.length > 0 &&
    typeof candidate.name === 'string' &&
    candidate.name.length > 0 &&
    typeof candidate.description === 'string' &&
    Array.isArray(candidate.courseSwatches) &&
    candidate.courseSwatches.length > 0 &&
    candidate.courseSwatches.every(isAcademicPaletteSwatch) &&
    Array.isArray(candidate.programSwatches) &&
    candidate.programSwatches.length > 0 &&
    candidate.programSwatches.every(isAcademicPaletteSwatch)
  );
}

export function UiPreferencesProvider({ children }: { children: ReactNode }) {
  const [activeSemester, setActiveSemesterState] = useState<SemesterId>(DEFAULT_SEMESTER_ID);
  const [preferredSchedulesBySemester, setPreferredSchedulesBySemester] = useState<Record<string, string>>({});
  const [isSidebarPinned, setIsSidebarPinnedState] = useState(true);
  const [sidebarPreferenceSaved, setSidebarPreferenceSaved] = useState(false);
  const [academicPalettePreferences, setAcademicPalettePreferencesState] =
    useState<AcademicPalettePreferences>(DEFAULT_ACADEMIC_PALETTE_PREFERENCES);
  const [customAcademicPalettes, setCustomAcademicPalettes] = useState<AcademicPalettePreset[]>([]);
  const [explorerPreferences, setExplorerPreferencesState] = useState<ExplorerPreferences>(
    DEFAULT_EXPLORER_PREFERENCES
  );
  const [explorerPreferencesHydrated, setExplorerPreferencesHydrated] = useState(false);

  useEffect(() => {
    const savedSemester = localStorage.getItem(ACTIVE_SEMESTER_STORAGE_KEY);
    if (isSemesterId(savedSemester)) setActiveSemesterState(savedSemester);

    const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (saved !== null) {
      setIsSidebarPinnedState(saved === 'true');
      setSidebarPreferenceSaved(true);
    }

    const savedSemesterSchedules = localStorage.getItem(SEMESTER_SCHEDULE_STORAGE_KEY);
    if (savedSemesterSchedules) {
      try {
        const parsed = JSON.parse(savedSemesterSchedules) as unknown;
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const normalized = Object.fromEntries(
            Object.entries(parsed).filter(
              ([semester, scheduleId]) =>
                typeof semester === 'string' &&
                semester.length > 0 &&
                typeof scheduleId === 'string' &&
                scheduleId.length > 0
            )
          ) as Record<string, string>;
          setPreferredSchedulesBySemester(normalized);
        }
      } catch (error) {
        console.warn('Failed to parse stored semester schedule preferences.', error);
      }
    }

    const savedAcademicPalettes = localStorage.getItem(ACADEMIC_PALETTE_STORAGE_KEY);
    if (savedAcademicPalettes) {
      try {
        const parsed = JSON.parse(savedAcademicPalettes) as unknown;
        if (isAcademicPalettePreferences(parsed)) {
          setAcademicPalettePreferencesState(parsed);
        }
      } catch (error) {
        console.warn('Failed to parse stored academic palette preferences.', error);
      }
    }

    const savedCustomAcademicPalettes = localStorage.getItem(CUSTOM_ACADEMIC_PALETTES_STORAGE_KEY);
    if (savedCustomAcademicPalettes) {
      try {
        const parsed = JSON.parse(savedCustomAcademicPalettes) as unknown;
        if (Array.isArray(parsed)) {
          setCustomAcademicPalettes(parsed.filter(isAcademicPalettePreset));
        }
      } catch (error) {
        console.warn('Failed to parse stored custom academic palettes.', error);
      }
    }

    const savedExplorerPreferences = localStorage.getItem(EXPLORER_PREFERENCES_STORAGE_KEY);
    let nextExplorerPreferences: ExplorerPreferences | null = null;
    if (savedExplorerPreferences) {
      try {
        nextExplorerPreferences = parseExplorerPreferences(JSON.parse(savedExplorerPreferences));
      } catch (error) {
        console.warn('Failed to parse stored Explorer preferences.', error);
      }
    }
    if (!nextExplorerPreferences) {
      const legacyView = localStorage.getItem(LEGACY_EXPLORER_VIEW_STORAGE_KEY);
      nextExplorerPreferences = {
        ...DEFAULT_EXPLORER_PREFERENCES,
        ...(isExplorerViewMode(legacyView) ? { defaultView: legacyView } : {}),
      };
      localStorage.setItem(
        EXPLORER_PREFERENCES_STORAGE_KEY,
        JSON.stringify(nextExplorerPreferences)
      );
      if (legacyView !== null) localStorage.removeItem(LEGACY_EXPLORER_VIEW_STORAGE_KEY);
    }
    setExplorerPreferencesState(nextExplorerPreferences);
    setExplorerPreferencesHydrated(true);
  }, []);

  useEffect(() => {
    const syncDevicePreferences = (event: StorageEvent) => {
      if (event.key === EXPLORER_PREFERENCES_STORAGE_KEY) {
        if (!event.newValue) {
          setExplorerPreferencesState(DEFAULT_EXPLORER_PREFERENCES);
          return;
        }
        try {
          const next = parseExplorerPreferences(JSON.parse(event.newValue));
          setExplorerPreferencesState(next ?? DEFAULT_EXPLORER_PREFERENCES);
        } catch (error) {
          console.warn('Failed to sync Explorer preferences.', error);
          setExplorerPreferencesState(DEFAULT_EXPLORER_PREFERENCES);
        }
        return;
      }

      if (event.key === ACADEMIC_PALETTE_STORAGE_KEY) {
        if (!event.newValue) {
          setAcademicPalettePreferencesState(DEFAULT_ACADEMIC_PALETTE_PREFERENCES);
          return;
        }
        try {
          const next = JSON.parse(event.newValue) as unknown;
          if (isAcademicPalettePreferences(next)) setAcademicPalettePreferencesState(next);
        } catch (error) {
          console.warn('Failed to sync academic palette preferences.', error);
        }
        return;
      }

      if (event.key !== CUSTOM_ACADEMIC_PALETTES_STORAGE_KEY) return;
      if (!event.newValue) {
        setCustomAcademicPalettes([]);
        return;
      }
      try {
        const next = JSON.parse(event.newValue) as unknown;
        if (Array.isArray(next)) setCustomAcademicPalettes(next.filter(isAcademicPalettePreset));
      } catch (error) {
        console.warn('Failed to sync custom academic palettes.', error);
      }
    };
    window.addEventListener('storage', syncDevicePreferences);
    return () => window.removeEventListener('storage', syncDevicePreferences);
  }, []);

  const academicPalettePresets = useMemo(
    () => [...ACADEMIC_PALETTE_PRESETS, ...customAcademicPalettes],
    [customAcademicPalettes]
  );

  const setActiveSemester = useCallback((semester: string) => {
    if (!isSemesterId(semester)) return;
    setActiveSemesterState(semester);
    localStorage.setItem(ACTIVE_SEMESTER_STORAGE_KEY, semester);
  }, []);

  const setSidebarPinned = useCallback((pinned: boolean) => {
    setIsSidebarPinnedState(pinned);
    setSidebarPreferenceSaved(true);
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(pinned));
  }, []);

  const toggleSidebarPin = useCallback(() => {
    setSidebarPreferenceSaved(true);
    setIsSidebarPinnedState((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const setAcademicPalettePreferences = useCallback(
    (preferences: AcademicPalettePreferences) => {
      setAcademicPalettePreferencesState(preferences);
      localStorage.setItem(ACADEMIC_PALETTE_STORAGE_KEY, JSON.stringify(preferences));
    },
    []
  );

  const setCoursePaletteId = useCallback(
    (paletteId: string) => {
      setAcademicPalettePreferencesState((prev) => {
        const next = { ...prev, coursePaletteId: paletteId };
        localStorage.setItem(ACADEMIC_PALETTE_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    },
    []
  );

  const setProgramPaletteId = useCallback(
    (paletteId: string) => {
      setAcademicPalettePreferencesState((prev) => {
        const next = { ...prev, programPaletteId: paletteId };
        localStorage.setItem(ACADEMIC_PALETTE_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    },
    []
  );

  const addCustomAcademicPalette = useCallback((preset: AcademicPalettePreset) => {
    setCustomAcademicPalettes((prev) => {
      const next = [...prev.filter((entry) => entry.id !== preset.id), { ...preset, isCustom: true }];
      localStorage.setItem(CUSTOM_ACADEMIC_PALETTES_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const deleteCustomAcademicPalette = useCallback((paletteId: string) => {
    setCustomAcademicPalettes((prev) => {
      const next = prev.filter((preset) => preset.id !== paletteId);
      localStorage.setItem(CUSTOM_ACADEMIC_PALETTES_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    setAcademicPalettePreferencesState((prev) => {
      const next = {
        coursePaletteId:
          prev.coursePaletteId === paletteId
            ? DEFAULT_ACADEMIC_PALETTE_PREFERENCES.coursePaletteId
            : prev.coursePaletteId,
        programPaletteId:
          prev.programPaletteId === paletteId
            ? DEFAULT_ACADEMIC_PALETTE_PREFERENCES.programPaletteId
            : prev.programPaletteId,
      };
      localStorage.setItem(ACADEMIC_PALETTE_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const resetAcademicPalettePreferences = useCallback(() => {
    setAcademicPalettePreferencesState(DEFAULT_ACADEMIC_PALETTE_PREFERENCES);
    localStorage.setItem(
      ACADEMIC_PALETTE_STORAGE_KEY,
      JSON.stringify(DEFAULT_ACADEMIC_PALETTE_PREFERENCES)
    );
  }, []);

  const setExplorerDefaultView = useCallback((view: ExplorerViewMode) => {
    if (!isExplorerViewMode(view)) return;
    setExplorerPreferencesState((prev) => {
      const next = { ...prev, defaultView: view };
      localStorage.setItem(EXPLORER_PREFERENCES_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const setExplorerDepartmentColorsEnabled = useCallback((enabled: boolean) => {
    setExplorerPreferencesState((prev) => {
      const next = { ...prev, departmentColorsEnabled: enabled };
      localStorage.setItem(EXPLORER_PREFERENCES_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const setPreferredScheduleForSemester = useCallback((semester: string, scheduleId: string) => {
    setPreferredSchedulesBySemester((prev) => {
      if (prev[semester] === scheduleId) return prev;
      const next = { ...prev, [semester]: scheduleId };
      localStorage.setItem(SEMESTER_SCHEDULE_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      activeSemester,
      setActiveSemester,
      preferredSchedulesBySemester,
      setPreferredScheduleForSemester,
      isSidebarPinned,
      sidebarPreferenceSaved,
      setSidebarPinned,
      toggleSidebarPin,
      academicPalettePreferences,
      academicPalettePresets,
      setAcademicPalettePreferences,
      setCoursePaletteId,
      setProgramPaletteId,
      addCustomAcademicPalette,
      deleteCustomAcademicPalette,
      resetAcademicPalettePreferences,
      explorerPreferences,
      explorerPreferencesHydrated,
      setExplorerDefaultView,
      setExplorerDepartmentColorsEnabled,
    }),
    [
      activeSemester,
      setActiveSemester,
      preferredSchedulesBySemester,
      setPreferredScheduleForSemester,
      isSidebarPinned,
      sidebarPreferenceSaved,
      setSidebarPinned,
      toggleSidebarPin,
      academicPalettePreferences,
      academicPalettePresets,
      setAcademicPalettePreferences,
      setCoursePaletteId,
      setProgramPaletteId,
      addCustomAcademicPalette,
      deleteCustomAcademicPalette,
      resetAcademicPalettePreferences,
      explorerPreferences,
      explorerPreferencesHydrated,
      setExplorerDefaultView,
      setExplorerDepartmentColorsEnabled,
    ]
  );

  return (
    <UiPreferencesContext.Provider value={value}>
      {children}
    </UiPreferencesContext.Provider>
  );
}

export function useUiPreferences() {
  const ctx = useContext(UiPreferencesContext);
  if (!ctx) {
    throw new Error('useUiPreferences must be used within UiPreferencesProvider');
  }
  return ctx;
}
